import { useState } from "react";
import { useAxios } from "../../utils/useAxios";
import { ERROR_MESSAGES } from "../../constant/constant";
import { TENANT_DOMAIN } from "../../utils/domain";

/**
 * @typedef {Object} QuestItem
 * @property {number} quest_id
 * @property {string} quest_name
 * @property {string} episodes
 * @property {number} percentage
 * @property {boolean} can_resume
 */

/**
 * @typedef {Object} StudentProgress
 * @property {number} overall_percentage
 * @property {number} total_quests
 * @property {number} completed_quests
 * @property {string} message
 * @property {QuestItem[]} quests
 */

/**
 * @typedef {Object} StudentProgressResponse
 * @property {number} id
 * @property {string} tenant_id
 * @property {number} inst_id
 * @property {string} name
 * @property {string} username
 * @property {string} email
 * @property {string} age
 * @property {number} role_id
 * @property {string} class_id
 * @property {string} student_code
 * @property {StudentProgress} progress
 */

/**
 * Hook to fetch School Student Progress
 *
 * @returns {{
 *  progressData: StudentProgressResponse | null,
 *  loading: boolean,
 *  error: string,
 *  getStudentProgress: () => Promise<void>
 * }}
 */
export const useGetSclStudentProgress = () => {
  const [progressData, setProgressData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const { axiosInstance } = useAxios();

  const getStudentProgress = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await axiosInstance.get(
        `${TENANT_DOMAIN}/student/progress`,
      );

      setProgressData(response?.data?.data || null);
    } catch (err) {
      setProgressData(null);
      if (err?.response?.status === 500) {
        setError(ERROR_MESSAGES.INTERNAL_SERVER);
      } else {
        setError(err?.response?.data?.message || ERROR_MESSAGES.UNKNOWN);
      }
    } finally {
      setLoading(false);
    }
  };

  return { progressData, loading, error, getStudentProgress };
};
